import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, onSessionExpired, restoreTokens, setTokens } from '../../lib/api';
import { identifyUser, resetTelemetry } from '../../lib/telemetry';

export interface AuthUser {
  id: string;
  email: string;
  role: 'owner' | 'manager' | 'dentist' | 'assistant' | 'receptionist';
  tenantId: string;
  /** Membro criado com senha temporária → precisa trocar no 1º acesso. */
  mustChangePassword?: boolean;
}

interface AuthContextShape {
  user: AuthUser | null;
  loading: boolean;
  /** `otp` só é enviado quando o 2FA está ativo (TOTP ou backup code). */
  login: (email: string, password: string, otp?: string) => Promise<void>;
  logout: () => void;
}

const Ctx = createContext<AuthContextShape | null>(null);

/**
 * Auto-logout por inatividade (segurança em computador compartilhado —
 * ex.: recepção). Depois de IDLE_LIMIT_MS sem NENHUMA atividade do usuário
 * (mouse, teclado, toque, scroll), a sessão é encerrada em TODAS as abas.
 *
 * O relógio é o timestamp da última atividade guardado no localStorage —
 * então atividade em qualquer aba mantém todas vivas, e o corte vale pra
 * todas de uma vez. O tempo é ajustável pela clínica (tenant.idleLogoutMinutes,
 * Configurações → Segurança); NULL = padrão. Cacheamos o valor em localStorage
 * pra o vigia já começar com o certo antes do /tenants/me responder.
 */
const DEFAULT_IDLE_LIMIT_MS = 15 * 60_000; // padrão do app
const IDLE_MIN_MINUTES = 5;
const IDLE_MAX_MINUTES = 480; // 8h
const IDLE_LIMIT_CACHE_KEY = 'dentalflow.idleLimitMs';
const IDLE_ACTIVITY_KEY = 'dentalflow.lastActivity';

/** Lê o limite cacheado (ms). Cai no padrão se ausente/ inválido. */
function readCachedIdleLimit(): number {
  try {
    const n = Number(localStorage.getItem(IDLE_LIMIT_CACHE_KEY) || 0);
    if (n >= IDLE_MIN_MINUTES * 60_000 && n <= IDLE_MAX_MINUTES * 60_000) {
      return n;
    }
  } catch {
    /* noop */
  }
  return DEFAULT_IDLE_LIMIT_MS;
}
/** Sinaliza pra tela de login que a sessão caiu por inatividade (não erro). */
export const IDLE_LOGOUT_FLAG = 'dentalflow.idleLogout';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const qc = useQueryClient();
  // Limite de inatividade efetivo (ms). Inicia do cache; atualizado quando
  // /tenants/me responde com idleLogoutMinutes. Ref porque o vigia lê o
  // valor a cada tick sem recriar o efeito.
  const idleLimitRef = useRef(readCachedIdleLimit());

  // Logout centralizado — usado pelo botão Sair, pela expiração de sessão
  // (401 sem refresh) e pelo auto-logout de inatividade.
  const doLogout = useCallback(
    (reason?: 'idle') => {
      setTokens(null, null);
      setUser(null);
      resetTelemetry();
      // CRÍTICO: limpa cache do React Query no logout. Sem isso, queries
      // continuam servindo dados do tenant até refetch — e no próximo login
      // no mesmo navegador vazaria tenant anterior (multi-tenant).
      qc.clear();
      if (reason === 'idle') {
        try {
          sessionStorage.setItem(IDLE_LOGOUT_FLAG, '1');
        } catch {
          /* storage indisponível — segue sem o aviso */
        }
      }
    },
    [qc],
  );

  useEffect(() => {
    const { access } = restoreTokens();
    if (!access) {
      setLoading(false);
      return;
    }
    // Decodifica claims localmente só para UX (a API valida de verdade).
    try {
      const payload = JSON.parse(atob(access.split('.')[1]));
      const u: AuthUser = {
        id: payload.sub,
        email: payload.email,
        role: payload.role,
        tenantId: payload.tenantId,
        mustChangePassword: payload.mustChangePassword,
      };
      setUser(u);
      // Hidrata identidade no PostHog em refresh de página (sessão
      // já existia, sem login flow).
      identifyUser({
        userId: u.id,
        email: u.email,
        role: u.role,
        tenantId: u.tenantId,
      });
    } catch {
      setTokens(null, null);
    }
    setLoading(false);
    onSessionExpired(() => {
      setUser(null);
      resetTelemetry();
      // CRÍTICO: limpa cache do React Query quando sessão expira.
      // Sem isso, próximo usuário a logar no mesmo navegador veria
      // brevemente dados do tenant anterior (cache hit antes do refetch).
      qc.clear();
    });

  }, []);

  // Vigia de inatividade — só ativo quando há usuário logado.
  useEffect(() => {
    if (!user) return;

    const bump = () => {
      try {
        localStorage.setItem(IDLE_ACTIVITY_KEY, String(Date.now()));
      } catch {
        /* storage indisponível */
      }
    };
    bump(); // zera o contador ao logar / restaurar sessão

    // Throttle: no máximo uma escrita a cada 5s (mousemove dispara muito).
    let last = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - last > 5_000) {
        last = now;
        bump();
      }
    };
    const events: (keyof WindowEventMap)[] = [
      'mousemove',
      'mousedown',
      'keydown',
      'wheel',
      'touchstart',
      'click',
    ];
    events.forEach((e) =>
      window.addEventListener(e, onActivity, { passive: true }),
    );

    const check = () => {
      const raw = Number(localStorage.getItem(IDLE_ACTIVITY_KEY) || 0);
      if (raw && Date.now() - raw >= idleLimitRef.current) doLogout('idle');
    };
    const iv = window.setInterval(check, 15_000);
    // Máquina suspensa / aba escondida: checa assim que volta ao foco.
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(iv);
    };
  }, [user, doLogout]);

  // Carrega o tempo de inatividade configurado pela clínica e atualiza o
  // vigia (o ref é lido a cada tick). Falha silenciosa mantém cache/padrão.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    api
      .get('/tenants/me')
      .then((r) => {
        if (cancelled) return;
        const min = r.data?.idleLogoutMinutes;
        const ms =
          typeof min === 'number' &&
          min >= IDLE_MIN_MINUTES &&
          min <= IDLE_MAX_MINUTES
            ? min * 60_000
            : DEFAULT_IDLE_LIMIT_MS;
        idleLimitRef.current = ms;
        try {
          localStorage.setItem(IDLE_LIMIT_CACHE_KEY, String(ms));
        } catch {
          /* noop */
        }
      })
      .catch(() => {
        /* mantém cache/padrão */
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const login = useCallback(
    async (email: string, password: string, otp?: string) => {
      // Limpa cache ANTES de logar — defense in depth contra resíduo de
      // sessão anterior (logout incompleto, signup → login no mesmo tab).
      // Multi-tenant: dados do tenant X NUNCA podem vazar pro tenant Y.
      qc.clear();
      try {
        sessionStorage.removeItem(IDLE_LOGOUT_FLAG);
      } catch {
        /* noop */
      }
      const { data } = await api.post('/auth/login', {
        email,
        password,
        // Só manda o campo quando há código — evita 400 do @Matches num
        // login sem 2FA (otp é opcional no DTO).
        ...(otp ? { otp } : {}),
      });
      setTokens(data.accessToken, data.refreshToken);
      try {
        localStorage.setItem(IDLE_ACTIVITY_KEY, String(Date.now()));
      } catch {
        /* noop */
      }
      setUser(data.user);
      identifyUser({
        userId: data.user.id,
        email: data.user.email,
        role: data.user.role,
        tenantId: data.user.tenantId,
      });
    },
    [qc],
  );

  const value = useMemo<AuthContextShape>(
    () => ({
      user,
      loading,
      login,
      logout: () => doLogout(),
    }),
    [user, loading, login, doLogout],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth outside provider');
  return v;
}
